import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { CoverImage } from "@/components/posts/CoverImage"
import { PostMeta } from "@/components/posts/PostMeta"
import { PostActions } from "@/components/posts/PostActions"
import { ReadingProgress } from "@/components/posts/ReadingProgress"
import { ArticleContent } from "@/components/posts/ArticleContent"
import { CommentSection } from "@/components/comments/CommentSection"
import { getPostBySlug, getUserById, getCommentsWithAuthors, enrichPostsWithTags, incrementPostView } from "@/lib/db"
import { Loader2 } from "lucide-react"
import type { PostWithAuthor, CommentWithAuthor } from "@/types"

const recentViewIncrements = new Map<string, number>()

function shouldIncrementView(postId: string): boolean {
  const now = Date.now()
  const lastIncrement = recentViewIncrements.get(postId) ?? 0
  if (now - lastIncrement < 5000) return false
  recentViewIncrements.set(postId, now)
  return true
}

export default function PostPage() {
  const { slug } = useParams<{ slug: string }>()
  const [post, setPost] = useState<PostWithAuthor | null>(null)
  const [comments, setComments] = useState<CommentWithAuthor[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refreshComments = async () => {
    if (!post) return
    try {
      const commentsData = await getCommentsWithAuthors(post.id)
      setComments(commentsData)
    } catch (err) {
      console.error("Failed to refresh comments:", err)
    }
  }

  useEffect(() => {
    async function loadPost() {
      if (!slug) return

      try {
        setLoading(true)
        const rawPost = await getPostBySlug(slug)

        if (!rawPost) {
          setError("Post not found")
          return
        }

        const shouldTrackView = shouldIncrementView(rawPost.id)
        const viewIncrementPromise = shouldTrackView
          ? incrementPostView(rawPost.id)
              .then(() => true)
              .catch(() => false)
          : Promise.resolve(false)

        const [enrichedPosts, author, commentsData, viewIncremented] = await Promise.all([
          enrichPostsWithTags([rawPost]),
          getUserById(rawPost.authorId),
          getCommentsWithAuthors(rawPost.id),
          viewIncrementPromise,
        ])

        if (author) {
          const postWithAuthor = {
            ...enrichedPosts[0],
            author,
            viewCount: enrichedPosts[0].viewCount + (viewIncremented ? 1 : 0),
          }
          setPost(postWithAuthor)
        }
        setComments(commentsData)
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load post")
      } finally {
        setLoading(false)
      }
    }

    loadPost()
  }, [slug])

  if (loading) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    )
  }

  if (error || !post) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Post Not Found</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">{error || "The post you're looking for doesn't exist."}</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <ReadingProgress />
      <article className="container mx-auto px-4 py-8">
        <div className="mx-auto max-w-3xl">
          {/* Cover Image */}
          {post.coverImage && <CoverImage src={post.coverImage} alt={post.title} priority className="mb-8" />}

          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl lg:text-5xl">{post.title}</h1>
          {post.subtitle && <p className="mt-4 text-xl text-gray-600 dark:text-gray-400">{post.subtitle}</p>}

          {/* Meta */}
          <div className="mt-6">
            <PostMeta post={post} showViews />
          </div>

          {/* Actions */}
          <div className="mt-6">
            <PostActions
              postId={post.id}
              slug={post.slug}
              title={post.title}
              upvoteCount={post.upvoteCount || 0}
              commentCount={post.commentCount || 0}
            />
          </div>

          {/* Content */}
          <ArticleContent content={post.content} className="mt-10" />

          {/* Comments */}
          <CommentSection postId={post.id} comments={comments} commentCount={post.commentCount || 0} onRefresh={refreshComments} />
        </div>
      </article>
    </>
  )
}
